import React from 'react';
import { motion } from 'framer-motion';

/**
 * Privacy Policy page component
 * 
 * @returns React component for the privacy policy page
 */
const PrivacyPolicy = () => {
  return (
    <>
      <section className="pt-36 pb-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-6">Privacy Policy</h1>
            <div className="h-1 w-20 bg-gold mb-8"></div>
            <p className="text-charcoal-light mb-4">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-gold my-8">
              <h3 className="text-xl font-bold mb-2">Privacy Notice</h3>
              <p>
                "By submitting any information to OptiGold PMO Consulting, you consent to the collection and use of your information for the purpose of scheduling and conducting a discovery call. Your data will be used solely to connect with you to better understand your needs. We do not sell or share your personal information with third parties."
              </p>
            </div>

            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
            <p className="mb-4">
              OptiGold PMO Consulting ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
            <p className="mb-6">
              Please read this Privacy Policy carefully. By accessing or using our website or services, you acknowledge that you have read, understood, and agree to be bound by all the terms of this Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">2. Information We Collect</h2>
            <p className="mb-2">
              We may collect information about you in various ways, including:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2"><strong>Personal Information:</strong> Name, email address, phone number, and other contact details you provide when contacting us or using our services.</li>
              <li className="mb-2"><strong>Business Information:</strong> Information about your organization, job title, and business needs.</li>
              <li className="mb-2"><strong>Assessment Data:</strong> Responses and results from our PMO Maturity Assessment or other interactive tools.</li>
              <li className="mb-2"><strong>Website Usage Data:</strong> Information about how you use our website, including IP address, browser type, referring/exit pages, and operating system.</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">3. How We Use Your Information</h2>
            <p className="mb-2">
              We may use the information we collect about you for various purposes, including:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Providing, maintaining, and improving our services</li>
              <li className="mb-2">Responding to your inquiries, comments, or questions</li>
              <li className="mb-2">Understanding your needs to provide better service</li>
              <li className="mb-2">Sending you information about our services, events, or other professional matters</li>
              <li className="mb-2">Analyzing website usage to improve user experience</li>
              <li className="mb-2">Protecting our rights, property, or safety</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">4. Information Sharing and Disclosure</h2>
            <p className="mb-4">
              <strong>We take your privacy very seriously. We do not sell or share your personal information with any third parties for any reason ever.</strong> Your information is only used internally to provide and improve our services to you.
            </p>
            <p className="mb-2">
              We may disclose your personal information only in the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">With your explicit consent</li>
              <li className="mb-2">To comply with applicable laws, regulations, or legal processes</li>
              <li className="mb-2">To protect our rights, property, or safety, or that of our users or others</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">5. Data Security</h2>
            <p className="mb-6">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, accidental loss, alteration, or destruction. However, no internet transmission is ever completely secure or error-free, and we cannot guarantee the absolute security of your data.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">6. Your Rights</h2>
            <p className="mb-2">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">The right to access and receive a copy of your personal information</li>
              <li className="mb-2">The right to rectify or update your personal information</li>
              <li className="mb-2">The right to request deletion of your personal information</li>
              <li className="mb-2">The right to restrict or object to processing of your personal information</li>
              <li className="mb-2">The right to data portability</li>
            </ul>
            <p className="mb-6">
              To exercise any of these rights, please contact us using the information provided in the "Contact Us" section below.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">7. Changes to This Privacy Policy</h2>
            <p className="mb-6">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. The most current version will always be posted on our website, and we will indicate the date it was last updated.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">8. Contact Us</h2>
            <p className="mb-2">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="ml-6 mb-6">
              <strong>Email:</strong> inquiries@optigold.com<br />
              <strong>Website:</strong> www.optigoldpmo.com
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
